import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronUp, MessageCircle, User, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import IdeaCommentsSheet from "./IdeaCommentsSheet";
import { formatDistanceToNow } from "date-fns";

interface IdeaCardProps {
  idea: any;
  hasVoted: boolean;
  onVote: (hasVoted: boolean) => void;
  currentUserId: string | null;
  isGrantReady?: boolean;
}

const IdeaCard = ({ idea, hasVoted, onVote, currentUserId, isGrantReady }: IdeaCardProps) => {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const navigate = useNavigate();
  const profile = idea.profiles;

  const handleVote = () => {
    if (!currentUserId) {
      navigate("/auth");
      return;
    }
    onVote(hasVoted);
  };

  const handleComments = () => {
    if (!currentUserId) {
      navigate("/auth");
      return;
    }
    setCommentsOpen(true);
  };

  return (
    <>
      <div className="break-inside-avoid mb-4 group">
        <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-colors">
          {idea.image_url && (
            <div className="relative overflow-hidden">
              <img
                src={idea.image_url}
                alt={idea.title}
                className="w-full object-cover max-h-72"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}

          <div className="p-4">
            <div className="flex flex-wrap gap-1.5 mb-2">
              <Badge variant="secondary" className="text-xs font-medium">
                {idea.category}
              </Badge>
              {idea.status === "draft" && (
                <Badge variant="outline" className="text-xs border-dashed text-muted-foreground">
                  Draft
                </Badge>
              )}
              {isGrantReady && (
                <Badge className="gap-1 text-xs bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
                  <Sparkles className="h-3 w-3" /> Grant-Ready
                </Badge>
              )}
              {idea.tags?.slice(0, 2).map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            <Link
              to={`/ideas/${idea.id}`}
              className="block font-semibold text-foreground leading-snug mb-1.5 hover:text-primary transition-colors"
            >
              {idea.title}
            </Link>
            {idea.description && (
              <p className="text-sm text-muted-foreground line-clamp-3">{idea.description}</p>
            )}

            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              {idea.user_id ? (
                <Link
                  to={`/profile/${idea.user_id}`}
                  className="flex items-center gap-2 min-w-0 flex-1 hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} className="h-6 w-6 rounded-full object-cover" alt="" />
                    ) : (
                      <User className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground truncate hover:text-primary transition-colors">
                    {profile?.display_name || "Anonymous"}
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <User className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <span className="text-xs text-muted-foreground truncate">Anonymous</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground ml-auto shrink-0">
                {formatDistanceToNow(new Date(idea.created_at), { addSuffix: true })}
              </span>
            </div>

            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleVote}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  hasVoted
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                <ChevronUp className={`h-4 w-4 ${hasVoted ? "text-primary" : ""}`} />
                {idea.vote_count}
              </button>
              <button
                onClick={handleComments}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground bg-secondary hover:bg-secondary/80 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Comments
              </button>
            </div>
          </div>
        </div>
      </div>
      {currentUserId && (
        <IdeaCommentsSheet
          open={commentsOpen}
          onOpenChange={setCommentsOpen}
          ideaId={idea.id}
          ideaTitle={idea.title}
          currentUserId={currentUserId}
        />
      )}
    </>
  );
};

export default IdeaCard;
